// QMTrendingCharts.js
import { useState, useEffect } from 'react';
import { Bar, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import moment from "moment";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend
);

// Colors configuration
const chartColors = {
    callsPerHour: "rgba(56, 126, 252, 0.8)",
    qmScore: "rgba(16, 185, 129, 0.8)",
    qmScoreAvg: "rgba(16, 185, 129, 1)",
    autofailRate: "rgba(239, 68, 68, 0.8)",
    autofailRateAvg: "rgba(239, 68, 68, 1)",
};

// Section colors configuration (15 distinct colors)
const sectionColors = [
    "rgba(59, 130, 246, 0.8)",   // Blue
    "rgba(16, 185, 129, 0.8)",   // Green
    "rgba(251, 146, 60, 0.8)",   // Orange
    "rgba(217, 70, 239, 0.8)",   // Purple
    "rgba(236, 72, 153, 0.8)",   // Pink
    "rgba(6, 182, 212, 0.8)",    // Cyan
    "rgba(250, 204, 21, 0.8)",   // Yellow
    "rgba(99, 102, 241, 0.8)",   // Indigo
    "rgba(168, 85, 247, 0.8)",   // Violet
    "rgba(34, 197, 94, 0.8)",    // Emerald
    "rgba(239, 68, 68, 0.8)",    // Red
    "rgba(14, 165, 233, 0.8)",   // Sky
    "rgba(245, 158, 11, 0.8)",   // Amber
    "rgba(220, 38, 38, 0.8)",    // Rose
    "rgba(8, 145, 178, 0.8)",    // Teal
    "rgba(192, 132, 252, 0.8)",  // Light Purple
    "rgba(251, 113, 133, 0.8)",  // Light Pink
    "rgba(74, 222, 128, 0.8)",   // Light Green
    "rgba(96, 165, 250, 0.8)",   // Light Blue
    "rgba(253, 186, 116, 0.8)",  // Light Orange
];

const groupDataByWeek = (labels, callsData, qmScoreData, autofailData, sectionTrends = []) => {
    const weekData = {};

    labels.forEach((date, index) => {
        // Get the start of the week (Sunday) for the current date
        const weekStart = moment(date).startOf("week").format("YYYY-MM-DD");
        const displayDate = moment(weekStart).format("MM/DD/YYYY");

        if (!weekData[displayDate]) {
            weekData[displayDate] = {
                totalCalls: 0,
                qmScoreSum: 0,
                qmScoreCount: 0,
                autofailSum: 0,
                autofailCount: 0,
                sections: {},
            };
        }

        weekData[displayDate].totalCalls += callsData[index] || 0;

        // Only add to QM score if it's a valid number
        if (qmScoreData[index] !== null && qmScoreData[index] !== 0 && !isNaN(qmScoreData[index])) {
            weekData[displayDate].qmScoreSum += qmScoreData[index];
            weekData[displayDate].qmScoreCount += 1;
        }

        // Only add to autofail if it's a valid number
        if (autofailData[index] !== null && autofailData[index] !== 0 && !isNaN(autofailData[index])) {
            weekData[displayDate].autofailSum += autofailData[index];
            weekData[displayDate].autofailCount += 1;
        }

        // Process section trends
        sectionTrends.forEach(section => {
            if (!weekData[displayDate].sections[section.sectionId]) {
                weekData[displayDate].sections[section.sectionId] = {
                    sum: 0,
                    count: 0,
                };
            }
            const sectionScore = section.sectionAverageScores[index];
            if (sectionScore !== null && sectionScore !== 0 && !isNaN(sectionScore)) {
                weekData[displayDate].sections[section.sectionId].sum += sectionScore;
                weekData[displayDate].sections[section.sectionId].count += 1;
            }
        });
    });

    const sortedWeeks = Object.keys(weekData).sort(
        (a, b) => moment(a, "MM/DD/YYYY").valueOf() - moment(b, "MM/DD/YYYY").valueOf()
    );

    const groupedLabels = sortedWeeks;
    const groupedCalls = sortedWeeks.map((week) => weekData[week].totalCalls);
    const groupedQMScores = sortedWeeks.map((week) =>
        weekData[week].qmScoreCount > 0
            ? weekData[week].qmScoreSum / weekData[week].qmScoreCount
            : 0
    );
    const groupedAutofail = sortedWeeks.map((week) =>
        weekData[week].autofailCount > 0
            ? weekData[week].autofailSum / weekData[week].autofailCount
            : 0
    );

    // Group section data
    const groupedSections = sectionTrends.map(section => ({
        ...section,
        sectionAverageScores: sortedWeeks.map((week) => {
            const sectionData = weekData[week].sections[section.sectionId];
            return sectionData && sectionData.count > 0
                ? sectionData.sum / sectionData.count
                : 0;
        })
    }));

    return {
        labels: groupedLabels,
        callsData: groupedCalls,
        qmScoreData: groupedQMScores,
        autofailData: groupedAutofail,
        sectionTrends: groupedSections,
    };
};

const groupDataByMonth = (labels, callsData, qmScoreData, autofailData, sectionTrends = []) => {
    const monthData = {};

    labels.forEach((date, index) => {
        const monthYear = moment(date).format("MMM YYYY");

        if (!monthData[monthYear]) {
            monthData[monthYear] = {
                totalCalls: 0,
                qmScoreSum: 0,
                qmScoreCount: 0,
                autofailSum: 0,
                autofailCount: 0,
                sections: {},
            };
        }

        monthData[monthYear].totalCalls += callsData[index] || 0;

        // Only add to QM score if it's a valid number
        if (qmScoreData[index] !== null && qmScoreData[index] !== 0 && !isNaN(qmScoreData[index])) {
            monthData[monthYear].qmScoreSum += qmScoreData[index];
            monthData[monthYear].qmScoreCount += 1;
        }

        // Only add to autofail if it's a valid number
        if (autofailData[index] !== null && autofailData[index] !== 0 && !isNaN(autofailData[index])) {
            monthData[monthYear].autofailSum += autofailData[index];
            monthData[monthYear].autofailCount += 1;
        }

        // Process section trends
        sectionTrends.forEach(section => {
            if (!monthData[monthYear].sections[section.sectionId]) {
                monthData[monthYear].sections[section.sectionId] = {
                    sum: 0,
                    count: 0,
                };
            }
            const sectionScore = section.sectionAverageScores[index];
            if (sectionScore !== null && sectionScore !== 0 && !isNaN(sectionScore)) {
                monthData[monthYear].sections[section.sectionId].sum += sectionScore;
                monthData[monthYear].sections[section.sectionId].count += 1;
            }
        });
    });

    const sortedMonths = Object.keys(monthData).sort(
        (a, b) => moment(a, "MMM YYYY").valueOf() - moment(b, "MMM YYYY").valueOf()
    );

    const groupedLabels = sortedMonths;
    const groupedCalls = sortedMonths.map((month) => monthData[month].totalCalls);
    const groupedQMScores = sortedMonths.map((month) =>
        monthData[month].qmScoreCount > 0
            ? monthData[month].qmScoreSum / monthData[month].qmScoreCount
            : 0
    );
    const groupedAutofail = sortedMonths.map((month) =>
        monthData[month].autofailCount > 0
            ? monthData[month].autofailSum / monthData[month].autofailCount
            : 0
    );

    // Group section data
    const groupedSections = sectionTrends.map(section => ({
        ...section,
        sectionAverageScores: sortedMonths.map((month) => {
            const sectionData = monthData[month].sections[section.sectionId];
            return sectionData && sectionData.count > 0
                ? sectionData.sum / sectionData.count
                : 0;
        })
    }));

    return {
        labels: groupedLabels,
        callsData: groupedCalls,
        qmScoreData: groupedQMScores,
        autofailData: groupedAutofail,
        sectionTrends: groupedSections,
    };
};

function QMTrendingCharts({
    trendData = { labels: [], rowCounts: [], averageQmScores: [], averageFailureRates: [], qmSectionTrends: [] },
    dateRange,
    dateGroupType = "hour",
}) {
    const [processedTrendData, setProcessedTrendData] = useState(trendData);
    const [effectiveDateGroupType, setEffectiveDateGroupType] = useState(dateGroupType);
    const avgQMScore = trendData.avgQMScore || 0;
    const avgQMAutofailRate = trendData.avgQMAutofailRate || 0;

    useEffect(() => {
        // Group data based on date range
        if (trendData && trendData.labels && trendData.labels.length > 0) {
            if (dateRange === "thisYear") {
                const groupedData = groupDataByWeek(
                    trendData.labels,
                    trendData.rowCounts || [],
                    trendData.averageQmScores || [],
                    trendData.averageFailureRates || [],
                    trendData.qmSectionTrends || []
                );
                setProcessedTrendData({
                    labels: groupedData.labels,
                    rowCounts: groupedData.callsData,
                    averageQmScores: groupedData.qmScoreData,
                    averageFailureRates: groupedData.autofailData,
                    qmSectionTrends: groupedData.sectionTrends,
                });
                setEffectiveDateGroupType("week");
            } else if (dateRange === "all") {
                const groupedData = groupDataByMonth(
                    trendData.labels,
                    trendData.rowCounts || [],
                    trendData.averageQmScores || [],
                    trendData.averageFailureRates || [],
                    trendData.qmSectionTrends || []
                );
                setProcessedTrendData({
                    labels: groupedData.labels,
                    rowCounts: groupedData.callsData,
                    averageQmScores: groupedData.qmScoreData,
                    averageFailureRates: groupedData.autofailData,
                    qmSectionTrends: groupedData.sectionTrends,
                });
                setEffectiveDateGroupType("month");
            } else {
                setProcessedTrendData(trendData);
                setEffectiveDateGroupType(dateGroupType);
            }
        }
    }, [dateRange, trendData, dateGroupType]);

    // Generate labels based on the date group type
    const generateLabels = (labels) => {
        if (effectiveDateGroupType === "hour") {
            // Generate hour labels (12 AM, 1 AM, etc.)
            return labels.map((dateStr) => {
                const date = new Date(dateStr);
                const timeDisplay = date.toLocaleString('en-US', { hour: 'numeric', hour12: true })

                return timeDisplay;
            });
        } else {
            // Use the processed labels for week/month
            return processedTrendData.labels || [];
        }
    };

    const chartLabels = generateLabels(trendData.labels);

    // Get the appropriate time unit for axis labels
    const getTimeAxisLabel = () => {
        switch (effectiveDateGroupType) {
            case "hour":
                return "Hour of Day";
            case "week":
                return "Week Starting";
            case "month":
                return "Month";
            default:
                return "Date";
        }
    };

    // Get the appropriate title suffix
    const getTitleSuffix = () => {
        switch (effectiveDateGroupType) {
            case "hour":
                return "Per Hour";
            case "week":
                return "Per Week";
            case "month":
                return "Per Month";
            default:
                return "Per Day";
        }
    };

    // Format labels for display
    const formatLabelForDisplay = (label, index) => {
        if (effectiveDateGroupType === "hour") {
            return label; // Already formatted
        } else if (effectiveDateGroupType === "month") {
            return label; // Already in MMM YYYY format
        } else if (effectiveDateGroupType === "week") {
            return label; // Already in MM/DD/YYYY format
        } else {
            // For day grouping, format the date
            return moment(label).format("MM/DD/YYYY");
        }
    };

    // Chart 1: Calls Per Hour/Week/Month
    const callsPerHourChart = () => {
        if (!processedTrendData.rowCounts) return null;

        const chartData = {
            labels: chartLabels,
            datasets: [
                {
                    label: "Calls",
                    data: processedTrendData.rowCounts,
                    backgroundColor: chartColors.callsPerHour,
                    borderColor: chartColors.callsPerHour,
                    borderWidth: 1,
                },
            ],
        };

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                title: {
                    display: true,
                    text: `Calls ${getTitleSuffix()}`,
                    font: {
                        size: 16,
                        weight: 'bold',
                    },
                },
                tooltip: {
                    callbacks: {
                        title: function (tooltipItems) {
                            const label = tooltipItems[0].label;
                            return formatLabelForDisplay(label);
                        },
                        label: function (context) {
                            return `Calls: ${context.parsed.y}`;
                        }
                    }
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: "Number of Calls",
                    },
                },
                x: {
                    title: {
                        display: true,
                        text: getTimeAxisLabel(),
                    },
                    ticks: {
                        autoSkip: true,
                        maxRotation: 45,
                        callback: function (value) {
                            const label = this.getLabelForValue(value);
                            return formatLabelForDisplay(label);
                        },
                    },
                },
            },
        };

        return (
            <div className="w-full lg:w-1/3 p-2" style={{ height: "400px" }}>
                <Bar options={chartOptions} data={chartData} />
            </div>
        );
    };

    // Chart 2: Average QM Score Per Hour/Week/Month with Average Line
    const qmScoreChart = () => {
        if (!processedTrendData.averageQmScores) return null;

        // Convert to percentages
        const scoreData = processedTrendData.averageQmScores.map(score => score * 100);

        // Create average line data
        const avgScore = (avgQMScore || 0) * 100;
        console.log("Avg??", avgScore);
        const avgLineData = Array(chartLabels.length).fill(avgScore);

        const chartData = {
            labels: chartLabels,
            datasets: [
                {
                    type: 'line',
                    label: "QM Score",
                    data: scoreData,
                    backgroundColor: chartColors.qmScore,
                    borderColor: chartColors.qmScore,
                    borderWidth: 2,
                    fill: false,
                    spanGaps: true,
                    pointBackgroundColor: chartColors.qmScore,
                    pointBorderColor: chartColors.qmScore,
                    pointRadius: 4,
                },
                {
                    type: 'line',
                    label: `Average (${avgScore.toFixed(1)}%)`,
                    data: avgLineData,
                    borderColor: chartColors.qmScoreAvg,
                    borderWidth: 2,
                    borderDash: [5, 5],
                    fill: false,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                },
            ],
        };

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                },
                title: {
                    display: true,
                    text: `Average QM Score ${getTitleSuffix()}`,
                    font: {
                        size: 16,
                        weight: 'bold',
                    },
                },
                tooltip: {
                    callbacks: {
                        title: function (tooltipItems) {
                            const label = tooltipItems[0].label;
                            return formatLabelForDisplay(label);
                        },
                        label: function (context) {
                            if (context.datasetIndex === 1) {
                                return `${context.dataset.label}`;
                            }
                            return `QM Score: ${context.parsed.y?.toFixed(1)}%`;
                        }
                    }
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    max: 100,
                    title: {
                        display: true,
                        text: "Score (%)",
                    },
                },
                x: {
                    title: {
                        display: true,
                        text: getTimeAxisLabel(),
                    },
                    ticks: {
                        autoSkip: true,
                        maxRotation: 45,
                        callback: function (value) {
                            const label = this.getLabelForValue(value);
                            return formatLabelForDisplay(label);
                        },
                    },
                },
            },
        };

        return (
            <div className="w-full lg:w-1/3 p-2" style={{ height: "400px" }}>
                <Line options={chartOptions} data={chartData} />
            </div>
        );
    };

    // Chart 3: Average Autofail Rate Per Hour/Week/Month with Average Line
    const autofailRateChart = () => {
        if (!processedTrendData.averageFailureRates) return null;

        // Convert to percentages
        const autofailData = processedTrendData.averageFailureRates.map(rate => rate * 100);

        // Create average line data
        const avgAutofailRate = (avgQMAutofailRate || 0) * 100;
        const avgLineData = Array(chartLabels.length).fill(avgAutofailRate);

        const chartData = {
            labels: chartLabels,
            datasets: [
                {
                    type: 'line',
                    label: "Autofail Rate",
                    data: autofailData,
                    backgroundColor: chartColors.autofailRate,
                    borderColor: chartColors.autofailRate,
                    borderWidth: 2,
                    fill: false,
                    spanGaps: true,
                    pointBackgroundColor: chartColors.autofailRate,
                    pointBorderColor: chartColors.autofailRate,
                    pointRadius: 4,
                },
                {
                    type: 'line',
                    label: `Average (${avgAutofailRate.toFixed(1)}%)`,
                    data: avgLineData,
                    borderColor: chartColors.autofailRateAvg,
                    borderWidth: 2,
                    borderDash: [5, 5],
                    fill: false,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                },
            ],
        };

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                },
                title: {
                    display: true,
                    text: `Average Autofail Rate ${getTitleSuffix()}`,
                    font: {
                        size: 16,
                        weight: 'bold',
                    },
                },
                tooltip: {
                    callbacks: {
                        title: function (tooltipItems) {
                            const label = tooltipItems[0].label;
                            return formatLabelForDisplay(label);
                        },
                        label: function (context) {
                            if (context.datasetIndex === 1) {
                                return `${context.dataset.label}`;
                            }
                            return `Autofail Rate: ${context.parsed.y?.toFixed(1)}%`;
                        }
                    }
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    max: 100,
                    title: {
                        display: true,
                        text: "Rate (%)",
                    },
                },
                x: {
                    title: {
                        display: true,
                        text: getTimeAxisLabel(),
                    },
                    ticks: {
                        autoSkip: true,
                        maxRotation: 45,
                        callback: function (value) {
                            const label = this.getLabelForValue(value);
                            return formatLabelForDisplay(label);
                        },
                    },
                },
            },
        };

        return (
            <div className="w-full lg:w-1/3 p-2" style={{ height: "400px" }}>
                <Line options={chartOptions} data={chartData} />
            </div>
        );
    };

    // Chart 4: QM Section Trends
    const sectionTrendsChart = () => {
        const sectionData = effectiveDateGroupType === "hour"
            ? trendData.qmSectionTrends
            : processedTrendData.qmSectionTrends;

        if (!sectionData || sectionData.length === 0) return null;

        const datasets = sectionData.map((section, index) => ({
            label: section.sectionLabel,
            data: section.sectionAverageScores,
            borderColor: sectionColors[index % sectionColors.length],
            backgroundColor: sectionColors[index % sectionColors.length],
            borderWidth: 2,
            fill: false,
            spanGaps: true,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.1,
        }));

        const chartData = {
            labels: chartLabels,
            datasets: datasets,
        };

        // Calculate min and max scores from all sections to determine chart scale
        let allScores = [];
        sectionData.forEach(section => {
            section.sectionAverageScores.forEach(score => {
                if (score !== null && !isNaN(score)) {
                    allScores.push(score);
                }
            });
        });

        let yAxisMin = 0;
        let yAxisMax = 100;

        if (allScores.length > 0) {
            const minScore = Math.round(Math.min(...allScores));
            const maxScore = Math.round(Math.max(...allScores));

            console.log("QMC", minScore, maxScore);

            // Apply the zoom formula: (max(0, minScore-5), min(100, maxScore + 5))
            yAxisMin = Math.max(0, minScore - 5);
            yAxisMax = Math.min(105, maxScore + 5);
        }

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,

            plugins: {
                legend: {
                    display: true,
                    position: 'left',
                    labels: {
                        padding: 15,
                        font: {
                            size: 11,
                        },
                        boxWidth: 15,
                    },
                },
                title: {
                    display: true,
                    text: `QM Section Performance ${getTitleSuffix()}`,
                    font: {
                        size: 16,
                        weight: 'bold',
                    },
                },
                tooltip: {
                    callbacks: {
                        title: function (tooltipItems) {
                            const label = tooltipItems[0].label;
                            return formatLabelForDisplay(label);
                        },
                        label: function (context) {
                            return `${context.dataset.label}: ${context.parsed.y?.toFixed(1)}%`;
                        }
                    }
                },
            },
            scales: {
                y: {
                    min: yAxisMin,
                    max: yAxisMax,
                    grace: yAxisMax > 95 ? '1%' : 0, // Add 2% grace (padding) only when scores are high
                    title: {
                        display: true,
                        text: "Score (%)",
                    },
                    ticks: {
                        callback: function (value) {
                            // Only show tick labels for values <= 100
                            return value <= 100 ? value : '';
                        }
                    }
                },
                x: {
                    title: {
                        display: true,
                        text: getTimeAxisLabel(),
                    },
                    ticks: {
                        autoSkip: true,
                        maxRotation: 45,
                        callback: function (value) {
                            const label = this.getLabelForValue(value);
                            return formatLabelForDisplay(label);
                        },
                    },
                },
            },
        };

        return (
            <div className="w-full p-2" style={{ height: "320px" }}>
                <Line options={chartOptions} data={chartData} />
            </div>
        );
    };

    return (
        <div className="mb-8">
            <div className="flex flex-wrap container mx-auto">
                {callsPerHourChart()}
                {qmScoreChart()}
                {autofailRateChart()}
            </div>
            <div className="container mx-auto">
                {sectionTrendsChart()}
            </div>
        </div>
    );
}

export default QMTrendingCharts;